/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { ChartType } from "~/generated/graphql";
import {
  Granularity,
  DateRange,
  ChartType as CostAnalysisChartType,
} from "~/components";
import { EstatePeriod } from "~/constants";
import {
  EstateGroupBy,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  TotalCostTable,
  useEstateRecordsCost,
} from "~/views/estate/components/Charts";
import { useEstateRecordsOrgUnitChartJob } from "./components/Charts/hooks";
import { ORG_UNIT_SORT_OPTIONS } from "./components/ParametersBar/types";
import { isTableDataSortable, sortDataByTotal } from "./helpers";

type Props = {
  period: EstatePeriod;
  granularity: Granularity;
  range: DateRange;
  setIsDataLoading: (loading: boolean) => void;
};

export function EstateRecordsOrgUnitView({
  period,
  granularity,
  range,
  setIsDataLoading,
}: Props) {
  const {
    startDate,
    endDate,
    isDataLoading,
    isChartDataLoading,
    isTableDataLoading,
    chartData,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
    jobId,
  } = useEstateRecordsOrgUnitChartJob({
    chartType: ChartType.OrgUnit,
    range,
  });

  useEffect(() => {
    setIsDataLoading(isDataLoading);
  }, [isDataLoading, setIsDataLoading]);

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
    firstColumnWidth,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period,
    startDate,
    endDate,
    chartType: CostAnalysisChartType.COST_PER_ORG_UNIT,
  });

  const sortedCostPerCategoryTableData = isTableDataSortable(sortField, period)
    ? sortDataByTotal(costPerGroupTableData, sortDirection)
    : costPerGroupTableData;

  return (
    <>
      <EstateRecordsGroupingChart
        data={costPerGroupChartData}
        granularity={granularity}
        loading={isChartDataLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
        chartType={CostAnalysisChartType.COST_PER_ORG_UNIT}
      />
      <TotalCostTable
        period={period}
        range={range}
        count={chartData?.chartMonthlyJob?.data?.count ?? 0}
        isLoading={isChartDataLoading}
        chartType={EstateGroupBy.ORG}
        totalTop={extraDetails?.totalTop ?? "-"}
        totalOther={extraDetails?.totalOther ?? "-"}
      />
      <EstateRecordsGroupingTable
        loading={isTableDataLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={ORG_UNIT_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={sortedCostPerCategoryTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        jobId={jobId}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </>
  );
}
